import { useEffect } from 'react';
import SEO from '../../../components/SEO';

export default function CommercialSecurityOakville() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToForm = () => {
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const faqs = [
    {
      question: "What security systems do Oakville corporate offices need?",
      answer: "Oakville corporate offices require integrated access control with visitor management, HD cameras for parking lots and entrances, glass break sensors, and ULC-certified monitoring. Many offices in the QEW corridor and downtown Oakville also implement biometric readers for executive areas and server rooms to protect sensitive data and intellectual property."
    },
    {
      question: "How does Oakville's affluent business environment affect security needs?",
      answer: "Oakville's high-end retail, professional services, and corporate headquarters face sophisticated theft attempts and require premium security solutions. Businesses benefit from discreet camera installations, advanced analytics, mobile app monitoring, and white-glove service. Insurance companies often mandate higher security standards for Oakville commercial properties."
    },
    {
      question: "What's the cost of commercial security in Oakville?",
      answer: "Oakville commercial security typically ranges from $2,800-$9,000 for installation plus $50-$150/month for monitoring. Professional offices average $3,500-$6,000, while high-end retail in downtown Oakville or Bronte Village invests $5,000-$10,000 for comprehensive systems including facial recognition and advanced analytics."
    }
  ];

  const neighborhoods = [
    "Downtown Oakville",
    "Bronte Village",
    "Kerr Village",
    "QEW Corridor",
    "Uptown Core",
    "Glen Abbey",
    "Palermo",
    "Iroquois Ridge",
    "River Oaks",
    "Old Oakville",
    "Clearview",
    "College Park"
  ];

  const otherCities = [
    { name: "Toronto", slug: "toronto" },
    { name: "Mississauga", slug: "mississauga" },
    { name: "Brampton", slug: "brampton" },
    { name: "Hamilton", slug: "hamilton" },
    { name: "Richmond Hill", slug: "richmond-hill" },
    { name: "Vaughan", slug: "vaughan" }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LocalBusiness",
        "@id": "https://alliancesecuritysystems.com/commercial-security/oakville#business",
        "name": "Alliance Security Systems - Commercial Security Oakville",
        "image": "https://public.readdy.ai/ai/img_res/e3a29f3e7757d6e6f2b0b22dd70f9ca2.jpg",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Oakville",
          "addressRegion": "ON",
          "addressCountry": "CA"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "43.4675",
          "longitude": "-79.6877"
        },
        "url": "https://alliancesecuritysystems.com/commercial-security/oakville",
        "telephone": "+1-888-458-9181",
        "priceRange": "$$$-$$$$",
        "areaServed": {
          "@type": "City",
          "name": "Oakville"
        },
        "serviceType": ["Commercial Security Systems", "Business Alarm Systems", "Commercial CCTV", "Access Control Systems"]
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://alliancesecuritysystems.com/commercial-security/oakville#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://alliancesecuritysystems.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Commercial Security",
            "item": "https://alliancesecuritysystems.com/commercial-security"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "Oakville",
            "item": "https://alliancesecuritysystems.com/commercial-security/oakville"
          }
        ]
      },
      {
        "@type": "FAQPage",
        "@id": "https://alliancesecuritysystems.com/commercial-security/oakville#faq",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      }
    ]
  };

  return (
    <>
      <SEO 
        title="Commercial Security Oakville | Business Alarm & Camera Systems"
        description="Professional commercial security systems in Oakville. ULC-certified monitoring, HD cameras, access control for corporate offices, retail & professional services. Trusted by 100+ Oakville businesses."
        keywords="commercial security Oakville, business alarm system Oakville, commercial CCTV Oakville, access control Oakville, Oakville business security"
        canonical="/commercial-security/oakville"
        structuredData={structuredData}
      />

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
          <img 
            src="https://readdy.ai/api/search-image?query=Oakville%20downtown%20waterfront%20business%20district%20modern%20office%20buildings%20corporate%20headquarters%20professional%20cityscape%20lakeside%20view%20elegant%20architecture%20evening%20lighting&width=1920&height=800&seq=comm-oakville-hero&orientation=landscape"
            alt="Commercial Security Oakville - Downtown Business District"
            title="Oakville Commercial Security Systems"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
          
          <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
            <nav className="flex items-center justify-center gap-2 text-sm mb-6" itemScope itemType="https://schema.org/BreadcrumbList">
              <span itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <a href="/" itemProp="item" className="hover:text-teal-400 transition-colors">
                  <span itemProp="name">Home</span>
                </a>
                <meta itemProp="position" content="1" />
              </span>
              <span className="text-white/60">/</span>
              <span itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <a href="/commercial-security" itemProp="item" className="hover:text-teal-400 transition-colors">
                  <span itemProp="name">Commercial Security</span>
                </a>
                <meta itemProp="position" content="2" />
              </span>
              <span className="text-white/60">/</span>
              <span itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <span itemProp="name" className="text-teal-400">Oakville</span>
                <meta itemProp="position" content="3" />
              </span>
            </nav>

            <h1 className="text-5xl font-bold mb-6">Commercial Security Oakville</h1>
            <p className="text-xl mb-8 text-white/90">Protecting Oakville's Corporate Excellence — Premium Security for Professional Services, Retail & Offices</p>
            <button 
              onClick={scrollToForm}
              className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
            >
              Get Free Security Assessment
            </button>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-gray-900 text-white py-8">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-4xl font-bold text-teal-400 mb-2">100+</div>
                <div className="text-sm text-gray-300">Oakville Commercial Clients</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-teal-400 mb-2">&lt;4 min</div>
                <div className="text-sm text-gray-300">Average Response Time</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-teal-400 mb-2">22+ yrs</div>
                <div className="text-sm text-gray-300">Serving Oakville Businesses</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-teal-400 mb-2">20%</div>
                <div className="text-sm text-gray-300">Avg Insurance Discount</div>
              </div>
            </div>
          </div>
        </section>

        {/* Commercial Security Challenges in Oakville */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-4">Commercial Security Challenges in Oakville</h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              One of Canada's most affluent communities requires sophisticated security solutions for high-end retail, corporate offices, and professional services
            </p>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gray-50 p-8 rounded-lg">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-store-2-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">High-End Retail Protection</h3>
                <p className="text-gray-600 mb-4">
                  Downtown Oakville and Bronte Village host luxury boutiques, jewelry stores, and premium retailers requiring discreet yet comprehensive security. Businesses need facial recognition, real-time analytics, and coordinated response to prevent organized retail crime targeting high-value merchandise averaging $25,000 per theft incident.
                </p>
                <div className="text-sm text-gray-500">
                  <strong>Stat:</strong> Oakville luxury retail theft increased 29% in 2024, targeting designer goods
                </div>
              </div>

              <div className="bg-gray-50 p-8 rounded-lg">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-building-2-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Corporate Headquarters Security</h3>
                <p className="text-gray-600 mb-4">
                  Oakville hosts numerous corporate headquarters and professional services firms along the QEW corridor and downtown core. These businesses require executive protection, visitor management, server room access control, and after-hours monitoring to safeguard intellectual property, client data, and sensitive business operations.
                </p>
                <div className="text-sm text-gray-500">
                  <strong>Stat:</strong> 63% of Oakville corporate offices implement biometric access for executive areas
                </div>
              </div>

              <div className="bg-gray-50 p-8 rounded-lg">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-shield-star-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Premium Service Expectations</h3>
                <p className="text-gray-600 mb-4">
                  Oakville businesses expect white-glove security service with mobile app monitoring, instant alerts, and rapid response. Professional services firms, medical offices, and financial advisors require PIPEDA-compliant systems with encrypted storage, audit trails, and seamless integration with existing IT infrastructure.
                </p>
                <div className="text-sm text-gray-500">
                  <strong>Stat:</strong> 78% of Oakville businesses prioritize mobile monitoring and remote management
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-4">Commercial Security Solutions for Oakville</h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Premium security systems tailored for Oakville's corporate, retail, and professional service sectors
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <a href="/camera-packages" className="bg-white p-8 rounded-lg hover:shadow-lg transition-shadow cursor-pointer group">
                <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-colors">
                  <i className="ri-camera-line text-3xl text-teal-600 group-hover:text-white transition-colors"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Commercial Cameras</h3>
                <p className="text-gray-600 mb-4">4K resolution, discreet installations, facial recognition, AI analytics for retail loss prevention and corporate monitoring</p>
                <div className="text-teal-600 font-semibold flex items-center gap-2">
                  Learn More <i className="ri-arrow-right-line"></i>
                </div>
              </a>

              <a href="/alarm-packages" className="bg-white p-8 rounded-lg hover:shadow-lg transition-shadow cursor-pointer group">
                <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-colors">
                  <i className="ri-alarm-warning-line text-3xl text-teal-600 group-hover:text-white transition-colors"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Business Alarms</h3>
                <p className="text-gray-600 mb-4">ULC-certified monitoring, glass break sensors, motion detectors, panic buttons, direct Halton Regional Police integration</p>
                <div className="text-teal-600 font-semibold flex items-center gap-2">
                  Learn More <i className="ri-arrow-right-line"></i>
                </div>
              </a>

              <a href="/access-control" className="bg-white p-8 rounded-lg hover:shadow-lg transition-shadow cursor-pointer group">
                <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-colors">
                  <i className="ri-key-line text-3xl text-teal-600 group-hover:text-white transition-colors"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Access Control</h3>
                <p className="text-gray-600 mb-4">Biometric readers, mobile credentials, visitor management, time-based access for corporate offices and professional services</p>
                <div className="text-teal-600 font-semibold flex items-center gap-2">
                  Learn More <i className="ri-arrow-right-line"></i>
                </div>
              </a>

              <a href="/live-monitoring" className="bg-white p-8 rounded-lg hover:shadow-lg transition-shadow cursor-pointer group">
                <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-colors">
                  <i className="ri-eye-line text-3xl text-teal-600 group-hover:text-white transition-colors"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Live Monitoring</h3>
                <p className="text-gray-600 mb-4">24/7 video verification, mobile app access, two-way audio intervention, real-time threat assessment</p>
                <div className="text-teal-600 font-semibold flex items-center gap-2">
                  Learn More <i className="ri-arrow-right-line"></i>
                </div>
              </a>
            </div>
          </div>
        </section>

        {/* Neighborhoods Served */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-4">Oakville Business Districts We Serve</h2>
            <p className="text-center text-gray-600 mb-12">
              Protecting businesses across all Oakville neighborhoods and commercial zones
            </p>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {neighborhoods.map((neighborhood, index) => (
                <div key={index} className="bg-gray-50 px-6 py-4 rounded-lg text-center hover:bg-teal-50 transition-colors">
                  <div className="font-semibold text-gray-800">{neighborhood}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-4">Oakville Commercial Security FAQs</h2>
            <p className="text-center text-gray-600 mb-12">
              Common questions from Oakville business owners
            </p>

            <div className="space-y-6" itemScope itemType="https://schema.org/FAQPage">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white p-8 rounded-lg" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                  <h3 className="text-xl font-bold mb-3 flex items-start gap-3" itemProp="name">
                    <span className="text-teal-500 flex-shrink-0">Q:</span>
                    <span>{faq.question}</span>
                  </h3>
                  <div className="pl-8 text-gray-600" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                    <p itemProp="text">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact-form" className="py-20 bg-white">
          <div className="max-w-2xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-4">Get Your Free Oakville Security Assessment</h2>
            <p className="text-center text-gray-600 mb-12">
              On-site evaluation, custom proposal, and transparent pricing — no obligation
            </p>

            <form className="space-y-6 bg-gray-50 p-8 rounded-lg" data-readdy-form id="oakville-commercial-form" action="https://readdy.ai/api/form/d6ggqdmqpmcju3gsd8og" method="POST">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold mb-2">Business Name</label>
                  <input 
                    type="text" 
                    name="business_name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Your Company Ltd."
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Your Name</label>
                  <input 
                    type="text" 
                    name="contact_name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="John Smith"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold mb-2">Phone</label>
                  <input 
                    type="tel" 
                    name="phone"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="905-XXX-XXXX"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Email</label>
                  <input 
                    type="email" 
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="john@company.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Business Address in Oakville</label>
                <input 
                  type="text" 
                  name="business_address"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="123 Lakeshore Rd E, Oakville, ON"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Industry</label>
                <select 
                  name="industry"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="">Select Industry</option>
                  <option value="retail">Retail</option>
                  <option value="office">Office / Corporate</option>
                  <option value="professional-services">Professional Services</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="hospitality">Hospitality</option>
                  <option value="financial">Financial Services</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Security Needs (optional)</label>
                <textarea 
                  name="security_details"
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Describe your security concerns, current system, or specific requirements..."
                ></textarea>
                <div className="text-xs text-gray-500 mt-1">Maximum 500 characters</div>
              </div>

              <button 
                type="submit"
                className="w-full bg-teal-500 hover:bg-teal-600 text-white py-4 rounded-lg font-semibold text-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                Request Free Assessment
              </button>
            </form>
          </div>
        </section>

        {/* Other Cities */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-center mb-12">Commercial Security in Other GTA Cities</h2>
            <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
              {otherCities.map((city, index) => (
                <a 
                  key={index}
                  href={`/commercial-security/${city.slug}`}
                  className="bg-white px-6 py-4 rounded-lg text-center hover:shadow-lg transition-shadow cursor-pointer group"
                >
                  <div className="font-semibold text-gray-800 group-hover:text-teal-600 transition-colors">{city.name}</div>
                </a>
              ))}
            </div>
            <div className="text-center mt-8">
              <a href="/commercial-security/service-areas" className="text-teal-600 font-semibold hover:text-teal-700 inline-flex items-center gap-2">
                View All Service Areas <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </section>

        {/* CTA Strip */}
        <section className="bg-gradient-to-r from-teal-600 to-teal-700 py-16">
          <div className="max-w-4xl mx-auto px-6 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">Ready to Protect Your Oakville Business?</h2>
            <p className="text-xl mb-8 text-white/90">
              Join 100+ Oakville companies trusting us with their security
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <button 
                onClick={scrollToForm}
                className="bg-white text-teal-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Free Assessment
              </button>
              <a 
                href="/commercial-security"
                className="border-2 border-white text-white hover:bg-white hover:text-teal-600 px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer inline-block"
              >
                View All Solutions
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}